import React, {useState, useEffect} from 'react';
import {useQueryClient} from "react-query";
import {useToast} from "../../../utils/contexts/toast-context";
import useApiUpdateMonthlyInvoice from "./api-hooks/useApiUpdateMonthlyInvoice";
import useApiCreateMonthlyInvoice from "./api-hooks/useApiCreateMonthlyInvoice";
import {CustomAxiosError} from "../../../logics/api-config";
import {useAccounting} from "../../../utils/contexts/accounting_context";
import Loading from "../ui/Loading";
import {formatDateTimeForInput} from "../../../logics/helpers";


interface Props {
    onClose: () => void;
}

const MonthlyInvoiceForm: React.FC<Props> = ({onClose}) => {
    const {displayError, displayMessage} = useToast();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);

    const {selectedYear, selectedPeriod, selectedInvoice, selectedMonthlyInvoice} = useAccounting()

    const putMutation = useApiUpdateMonthlyInvoice();
    const postMutation = useApiCreateMonthlyInvoice();

    const [month, setMonth] = useState<number>();
    const [year, setYear] = useState<number>();
    const [restaurateur, setRestaurateur] = useState<number>();
    const [invoice_id, setInvoice_id] = useState<string>();
    const [invoice_info, setInvoice_info] = useState<string>();
    const [invoice_date, setInvoice_date] = useState<string>();
    const [net_amount, setNet_amount] = useState<number>();

    useEffect(() => {
        selectedYear && setYear(selectedYear.year);
        selectedPeriod && setMonth(selectedPeriod.month);
        selectedInvoice && setRestaurateur(selectedInvoice.restaurateur.id)
        if (selectedMonthlyInvoice) {
            setMonth(selectedMonthlyInvoice.month)
            setYear(selectedMonthlyInvoice.year)
            setRestaurateur(selectedMonthlyInvoice.restaurateur)
            setInvoice_id(selectedMonthlyInvoice.invoice_id)
            setInvoice_info(selectedMonthlyInvoice.invoice_info)
            setInvoice_date(selectedMonthlyInvoice.invoice_date)
            setNet_amount(selectedMonthlyInvoice.net_amount)
        }
    }, [selectedMonthlyInvoice]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();


        try {
            setIsLoading(true);

            const dateValue: string = invoice_date || '';
            const invoiceDate: Date = new Date(dateValue);

            const data = {
                month: month,
                year: year,
                restaurateur: restaurateur,
                invoice_id: invoice_id,
                invoice_info: invoice_info,
                invoice_date: new Date(invoiceDate),
                // invoice_date: invoice_date,
                net_amount: net_amount,
            };

            if (selectedMonthlyInvoice !== undefined) {
                await putMutation.mutateAsync({
                    id: selectedMonthlyInvoice.id,
                    data: data,
                });
                await queryClient.invalidateQueries('getMonthlyInvoicesDetails');
                displayMessage('MonthlyInvoice updated successfully');
            } else {
                await postMutation.mutateAsync(data);
                await queryClient.invalidateQueries('getMonthlyInvoicesDetails');
                displayMessage('MonthlyInvoice added successfully');
            }

            onClose();
        } catch (error) {
            const axiosError = error as CustomAxiosError;
            if (axiosError.response && axiosError.response.data && axiosError.response.data.message) {
                displayError(axiosError.response.data.message);
            } else {
                displayError('An error occurred while saving the MonthlyInvoice');
            }
        } finally {
            setIsLoading(false);
            await queryClient.invalidateQueries('getMonthlyInvoices');
        }
    };


    return (
        <>
            {isLoading ? (<Loading label='please wait...'/>) : (
                <div className="p-6 space-y-6">
                    <form onSubmit={handleSubmit}>
                        <div className="flex flex-col mt-2">
                            <label className="text-lg font-semibold mb-1">Invoice ID:</label>
                            <input
                                type="text"
                                value={invoice_id}
                                onChange={(e) => setInvoice_id(e.target.value)}
                                className="px-3 py-2 border rounded-md bg-mcia_gray_soft"
                            />
                        </div>
                        <div className="flex flex-col mt-2">
                            <label className="text-lg font-semibold mb-1">Invoice Date:</label>
                            <input
                                type="date"
                                value={formatDateTimeForInput(invoice_date)}
                                onChange={(e) => setInvoice_date(e.target.value)}
                                className="px-3 py-2 border rounded-md"
                            />
                        </div>
                        <div className="flex flex-col mt-2">
                            <label className="text-lg font-semibold mb-1">Net Amount:</label>
                            <input
                                type="number"
                                step='any'
                                value={net_amount}
                                onChange={(e) => setNet_amount(parseFloat(e.target.value))}
                                className="px-3 py-2 border rounded-md bg-mcia_gray_soft"
                            />
                        </div>
                        <div className="flex flex-col mt-2">
                            <label className="text-lg font-semibold mb-1">Invoice Info:</label>
                            <textarea
                                value={invoice_info}
                                onChange={(e) => setInvoice_info(e.target.value)}
                                className="px-3 py-2 border rounded-md bg-mcia_gray_soft"
                            ></textarea>
                        </div>

                        <div className="flex justify-between mt-5">
                            <div className='m-1'>
                                <button
                                    onClick={onClose}
                                    className="mt-2 p-4 py-2 bg-red-400 text-lg font-semibold text-white rounded-md hover:bg-red-500 focus:outline-none"
                                >
                                    CANCEL
                                </button>
                            </div>
                            <div className='m-1'>
                                <button
                                    type="submit"
                                    className="mt-2 p-4 py-2 text-lg font-semibold bg-mcia_gray_middle text-white rounded-md hover:bg-mcia_orange focus:outline-none"
                                >
                                    {selectedMonthlyInvoice ? 'UPDATE' : 'ADD'} INVOICE
                                </button>
                            </div>

                        </div>
                    </form>
                </div>)}
        </>

    );
};

export default MonthlyInvoiceForm;